import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { CODICI, FornitoreLingua, PRINCIPALE, useTraduzioni } from '../../i18n';
import { useCampanella, quandoNotifica } from '../features/NotificationPoller';
import Icona from './Icone';
import './cliente.css';

// Contenitore di tutte le pagine del cliente: barra in alto (desktop) e barra a cinque icone in basso
// (sotto i 900px), come nella bozza design/home-cliente-v8.html.
// nuovoStile = la pagina è già nel nuovo design (oggi la home): niente contenitore del vecchio stile,
// ma bagliore e grana della bozza.

// etichetta = la chiave del dizionario (il testo arriva da useTraduzioni: il guscio parla la lingua del cliente)
export const VOCI_CLIENTE = [
  { to: '/dashboard', etichetta: 'comune.menu.percorso', icona: 'casa', attiva: (p) => p === '/dashboard' },
  { to: '/immobili', etichetta: 'comune.menu.immobili', icona: 'immobili', attiva: (p) => p.startsWith('/immobili') },
  { to: '/calendario', etichetta: 'comune.menu.visite', icona: 'calendario', attiva: (p) => p === '/calendario' || p.startsWith('/visite') },
  { to: '/documenti', etichetta: 'comune.menu.documenti', icona: 'documento', attiva: (p) => p.startsWith('/documenti') },
  { to: '/profilo', etichetta: 'comune.menu.profilo', icona: 'profilo', attiva: (p) => p.startsWith('/profilo') },
];

const chiudiFuori = (aperto, chiudi, ref) => {
  if (!aperto) return undefined;
  const fuori = (e) => { if (ref.current && !ref.current.contains(e.target)) chiudi(); };
  const esc = (e) => { if (e.key === 'Escape') chiudi(); };
  document.addEventListener('mousedown', fuori);
  document.addEventListener('keydown', esc);
  return () => { document.removeEventListener('mousedown', fuori); document.removeEventListener('keydown', esc); };
};

// Le notifiche del cliente (push e campanella, senza doppioni: backend GET /notifiche), ognuna un link alla sua
// pagina; aprendo la tendina si segnano lette. Orari come nelle Novità della home. Mentre l'elenco arriva "Carico…",
// se non arriva lo dice con "Riprovate" (mai un falso "Nessuna notifica").
const Campanella = () => {
  const { unreadCount, showDropdown, setShowDropdown, notifiche, statoElenco, riprova, handleOpen } = useCampanella();
  const { t, tp, l } = useTraduzioni();
  const ref = useRef(null);
  useEffect(() => chiudiFuori(showDropdown, () => setShowDropdown(false), ref), [showDropdown, setShowDropdown]);
  return (
    <div className="menu-ancora" ref={ref}>
      <button type="button" className={`ib${unreadCount > 0 ? ' bell' : ''}`} onClick={handleOpen}
        aria-label={unreadCount > 0 ? `${t('comune.menu.notifiche')}: ${tp('comune.plurali.notifiche_nuove', unreadCount)}` : t('comune.menu.notifiche')}
        aria-expanded={showDropdown} data-testid="notification-bell">
        <Icona nome="campanella" />
      </button>
      {showDropdown && (
        <div className="tendina notifiche" role="dialog" aria-label={t('comune.menu.notifiche')}>
          <h3>{t('comune.menu.notifiche')}</h3>
          {statoElenco === 'attesa' ? <p className="vuoto attesa-notifiche" role="status">{t('comune.notifiche_carico')}</p>
            : statoElenco === 'errore' ? (
              <p className="vuoto errore-notifiche" role="alert">
                {t('comune.notifiche_non_disponibili')}{' '}
                <button type="button" className="riprova" onClick={riprova}>{t('comune.riprova')}</button>
              </p>
            ) : notifiche.length === 0 ? <p className="vuoto">{t('comune.notifiche_vuote')}</p> : (
            <ol>
              {notifiche.map((n, i) => (
                <li key={n.id || `${n.sent_at}-${i}`} className={n.letta === false ? 'nuova' : undefined}>
                  <Link to={n.url || '/dashboard'} onClick={() => setShowDropdown(false)} data-testid="voce-notifica">
                    <b>{n.letta === false && <><i className="pallino" aria-hidden="true" /><em className="sr-only">{t('comune.notifiche_nuova')} </em></>}{n.title}</b>
                    {n.body && <span>{n.body}</span>}
                    {/* "adesso" di Roma (il valore di base di quandoNotifica): la notifica si legge nell'ora di Roma e
                        con new Date() la si confrontava con l'ora del telefono, e fuori dal fuso "Oggi" e "Ieri" sbagliavano */}
                    <small>{quandoNotifica(n.sent_at, undefined, l)}</small>
                  </Link>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
};

const MenuProfilo = () => {
  const { user, logout } = useAuth();
  const { t } = useTraduzioni();
  const navigate = useNavigate();
  const [aperto, setAperto] = useState(false);
  const ref = useRef(null);
  useEffect(() => chiudiFuori(aperto, () => setAperto(false), ref), [aperto]);
  const esci = () => {
    logout();
    navigate('/login');
    toast.success(t('comune.siete_usciti'));
  };
  return (
    <div className="menu-ancora" ref={ref}>
      <button type="button" className="avatar" onClick={() => setAperto(!aperto)} aria-label={t('comune.menu.profilo_uscita')} aria-expanded={aperto}>
        {(user?.nome || '?').charAt(0).toUpperCase()}
      </button>
      {aperto && (
        <div className="tendina" role="menu">
          <div className="chi"><b>{[user?.nome, user?.cognome].filter(Boolean).join(' ')}</b><small>{user?.email}</small></div>
          <Link to="/profilo" role="menuitem" onClick={() => setAperto(false)}><Icona nome="profilo" />{t('comune.menu.profilo')}</Link>
          <button type="button" role="menuitem" className="esci" onClick={esci}><Icona nome="esci" />{t('comune.menu.esci')}</button>
        </div>
      )}
    </div>
  );
};

const NavCliente = () => {
  const { pathname } = useLocation();
  const { t } = useTraduzioni();
  return (
    <nav className="nav-cliente" aria-label={t('comune.menu.principale')}>
      <div className="wrap">
        <Link className="brand" to="/dashboard"><img src="/mk_logo_original.png" alt="" />MK<i>.</i>Consulting<span>Client</span></Link>
        <ul>
          {VOCI_CLIENTE.map((v) => (
            <li key={v.to}><Link to={v.to} className={v.attiva(pathname) ? 'on' : undefined} aria-current={v.attiva(pathname) ? 'page' : undefined}>{t(v.to === '/dashboard' ? 'comune.menu.percorso_lungo' : v.etichetta)}</Link></li>
          ))}
        </ul>
        <Campanella />
        <MenuProfilo />
      </div>
    </nav>
  );
};

const BarraCliente = () => {
  const { pathname } = useLocation();
  const { t } = useTraduzioni();
  return (
    <nav className="barra-cliente" aria-label={t('comune.menu.in_basso')}>
      {VOCI_CLIENTE.map((v) => (
        <Link key={v.to} to={v.to} className={v.attiva(pathname) ? 'on' : undefined} aria-current={v.attiva(pathname) ? 'page' : undefined}>
          <Icona nome={v.icona} />{t(v.etichetta)}
        </Link>
      ))}
    </nav>
  );
};

const LayoutCliente = ({ children, nuovoStile = false }) => {
  // La lingua del cliente (profilo, campo `lingua`) vale per tutte le pagine qui dentro: useTraduzioni() la
  // prende da qui (i18n.js). Lo staff non passa da questo contenitore e resta in italiano.
  const { lingua } = useAuth();

  // Il banner delle notifiche del browser (fuori dal router) deve stare sopra la barra in basso
  useEffect(() => {
    document.documentElement.classList.add('mk-barra-cliente');
    return () => document.documentElement.classList.remove('mk-barra-cliente');
  }, []);

  // <html lang>: in che lingua è la pagina. Lo leggono la sintesi vocale e il correttore del browser, che prima
  // leggevano il russo come se fosse italiano. Fuori dall'app del cliente (staff, accesso) torna l'italiano.
  useEffect(() => {
    document.documentElement.lang = CODICI.includes(lingua) ? lingua : PRINCIPALE;
    return () => { document.documentElement.lang = PRINCIPALE; };
  }, [lingua]);

  return (
    <FornitoreLingua lingua={lingua}>
      <div className="mk-cliente">
        {nuovoStile && <><div className="glow" /><div className="grain" /></>}
        <NavCliente />
        <div className="sopra-barra">
          {nuovoStile ? children : <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">{children}</main>}
        </div>
        <BarraCliente />
      </div>
    </FornitoreLingua>
  );
};

export default LayoutCliente;
